import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from "date-fns";
import { IoLocationOutline, IoPersonOutline, IoCalendarClearOutline, IoPeopleOutline  } from "react-icons/io5";
import { MdExpandMore, MdExpandLess } from "react-icons/md";
import { Exercise } from "../../../../models/Exercise";
import getId from "../../../../../utils/getId";
import EAChip from "../../../shared/Chip/EAChip";
import { MdError } from "react-icons/md";

interface IProps {
  exercise: Exercise;
  onCoachImageClick: () => void;
  description?: string;
}

const ExerciseInfoBox: React.FC<IProps> = ({ exercise, onCoachImageClick, description }) => {
  const { t } = useTranslation();
  const hasDeputy = getId(exercise.deputyHumanIds);
  // Jeśli jest zastępca, pokaż tylko jego zdjęcie (lub brak); jeśli nie ma zastępcy, pokaż zdjęcie instruktora
  const displayPicture = hasDeputy ? exercise.deputyHumanPicture : exercise.humanPicture;
  const hasImage = displayPicture && displayPicture.length > 1;
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const descriptionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (descriptionRef.current && description) {
      // Check if content is truncated (more than 3 lines)
      const lineHeight = parseInt(window.getComputedStyle(descriptionRef.current).lineHeight);
      const maxHeight = lineHeight * 3;
      setShowExpandButton(descriptionRef.current.scrollHeight > maxHeight);
    }
  }, [description]);

  const getExerciseInfoBoxItem = (icon: React.ReactNode, label: string, value: string, isValueError?: boolean) => (
    <div className="exercise-info-box__item">
      {icon}
      <div className="exercise-info-box__item-content">
        <span className="body-text-small text-color-dark text-bold">{label}</span>
        {(isValueError && (
          <span className="body-text-small text-color-error flex-start-center text-bold">
            <MdError style={{ marginRight: "2px" }} />
            {value}
          </span>
        )) || <span className="body-text-small text-color-dark">{value}</span>}
      </div>
    </div>
  );

  const totalReserved = exercise.reserved + exercise.prereservation;
  const availableSpots = exercise.capacity - totalReserved;
  const hasNoSpaces = availableSpots <= 0;

  return (
    <div className="exercise-info-box">
      <div className="exercise-info-box__grid">
        {/* Data i godzina */}
        {getExerciseInfoBoxItem(
          <IoCalendarClearOutline className="exercise-info-box__item-icon" />,
          t("data_i_godzina"),
          format(exercise.startTime, "dd.MM.yyyy HH:mm"),
        )}

        {/* Strefa */}
        {getExerciseInfoBoxItem(
          <IoLocationOutline className="exercise-info-box__item-icon" />,
          t("strefa"),
          exercise.zone?.displayName || "",
        )}

        {/* Miejsca */}
        {getExerciseInfoBoxItem(
          <IoPeopleOutline className="exercise-info-box__item-icon" />,
          t("zajete_miejsca"),
          `${exercise.reserved + exercise.prereservation}/${exercise.capacity}`,
          hasNoSpaces,
        )}

        {/* Trener - Variant with image */}
        {exercise.humanName && exercise.humanName.length > 1 && hasImage && (
          <div className="exercise-info-box__coach">
            <div
              className="exercise-info-box__coach-image"
              onClick={onCoachImageClick}
              role="button"
              tabIndex={0}
              style={{ backgroundImage: `url(${displayPicture})` }}
            />
            <div className="exercise-info-box__item-content">
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span className="body-text-small text-color-dark text-bold">{t("trener")}</span>
                {getId(exercise.deputyHumanIds) && <EAChip label={t("zastepstwo")} variant="warning" />}
              </div>
              <span className="body-text-small text-color-dark">
                {getId(exercise.deputyHumanIds) ? (
                  <>
                    <s>{exercise.humanName}</s> {exercise.deputyHumanName}
                  </>
                ) : (
                  exercise.humanName
                )}
              </span>
            </div>
          </div>
        )}

        {/* Trener - Variant without image */}
        {exercise.humanName && exercise.humanName.length > 1 && !hasImage && (
          <div className="exercise-info-box__item">
            <IoPersonOutline className="exercise-info-box__item-icon" />
            <div className="exercise-info-box__item-content">
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span className="body-text-small text-color-dark text-bold">{t("trener")}</span>
                {getId(exercise.deputyHumanIds) && <EAChip label={t("zastepstwo")} variant="warning" />}
              </div>
              <span className="body-text-small text-color-dark">
                {getId(exercise.deputyHumanIds) ? (
                  <>
                    <s>{exercise.humanName}</s> {exercise.deputyHumanName}
                  </>
                ) : (
                  exercise.humanName
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Opis zajęć */}
      {description && description.length > 0 && (
        <div className="exercise-info-box__description">
          <div className="exercise-info-box__description-header">
            <h6>{t("opis_zajec")}</h6>
          </div>
          <article
            ref={descriptionRef}
            className={
              isDescriptionExpanded
                ? "exercise-info-box__description-content--expanded"
                : "exercise-info-box__description-content"
            }
            dangerouslySetInnerHTML={{ __html: description }}
          />
          {showExpandButton && (
            <span
              onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              className="caption text-color-dark-gray flex-start-center cursor-pointer"
            >
              {t(isDescriptionExpanded ? "pokaz_mniej" : "przeczytaj_caly_opis")}{" "}
              {isDescriptionExpanded ? <MdExpandLess /> : <MdExpandMore />}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ExerciseInfoBox;
